
import $ from 'jquery';
import Vue from 'vue';
import { Component, Inject, Prop } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { PqtApiUiV2Client, PqtProduct, PqtProductTest } from '../../backend/backendapi';
import PqtProductTestEdit from './PqtProductTestEdit';
import State from '../State';
import { handleError, initClient } from '../../../api';
import Spinner from '../../util/Spinner';
import moment from 'moment';

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    PqtProductTestEdit,
    Spinner,
  },
})
export default class PqtProductTestEditModal extends Vue {
  @Prop({ default: true })
  editable!: boolean;

  @Inject({ from: 'state' })
  readonly state!: State;

  private readonly productsApi = initClient(PqtApiUiV2Client);

  product: PqtProduct | null = null;
  test: PqtProductTest | null = null;
  newTestFiles: string[] = [];
  saving = false;

  get canSave(): boolean {
    return !!this.test
        && (!this.state.config.requiredFields?.includes('test.testDate') || !!this.test.testDate)
        && (!this.state.config.requiredFields?.includes('test.protocolNumber') || !!this.test.protocolNumber)
        && (!this.state.config.requiredFields?.includes('test.testPerson') || !!this.test.testPerson)
        && (!this.state.config.requiredFields?.includes('test.testSpecification') || !!this.test.testSpecification)
        && (!this.state.config.requiredFields?.includes('test.expectedError') || !!this.test.expectedError)
        && (!this.state.config.requiredFields?.includes('test.error') || !!this.test.error)
        && (!this.state.config.requiredFields?.includes('test.grade') || !!this.test.grade)
        && (!this.state.config.requiredFields?.includes('test.note') || !!this.test.note)
        && (!this.state.config.requiredFields?.includes('test.weight') || typeof this.test.weight === 'number')
        && (!this.state.config.requiredFields?.includes('test.weightUnit') || !!this.test.weightUnit)
        && (!this.state.config.requiredFields?.includes('test.batchNumber') || !!this.test.batchNumber);
  }

  public open(product: PqtProduct, test: PqtProductTest): void {
    this.product = product;
    this.test = test;
    this.newTestFiles.splice(0);
    this.autoFillHiddenRequiredFields();
    $(this.$refs.modal!).modal('show');
  }

  public dismiss(): void {
    $(this.$refs.modal!).modal('hide');
    this.product = null;
    this.test = null;
    this.newTestFiles.splice(0);
  }

  autoFillHiddenRequiredFields(): void {
    if (!this.product || !this.test) {
      return;
    }

    const isHiddenRequired = (key: string) => this.state.config.hiddenFields?.includes(key)
        && this.state.config.requiredFields?.includes(key);

    if (isHiddenRequired('test.protocolNumber')) this.test.protocolNumber ||= moment().format('YYYYmmdd');
    if (isHiddenRequired('test.testDate')) this.test.testDate ||= new Date();
    if (isHiddenRequired('test.testPerson')) this.test.testPerson ||= 'n/a';
    if (isHiddenRequired('test.testSpecification')) this.test.testSpecification ||= this.product.testSpecification;
    if (isHiddenRequired('test.expectedError')) this.test.expectedError ||= this.product.errorMargin;
    if (isHiddenRequired('test.error')) this.test.error = this.test.expectedError;
    if (isHiddenRequired('test.grade')) this.test.grade ||= 'n/a';
    if (isHiddenRequired('test.weight')) this.test.weight ??= 0;
    if (isHiddenRequired('test.weightUnit')) this.test.weightUnit = 'n/a';
    if (isHiddenRequired('test.batchNumber')) this.test.batchNumber = 'n/a';
  }

  async fileAdded(fileId: string): Promise<void> {
    if (!this.test) {
      return;
    }

    if (!this.test.id) {
      this.newTestFiles.push(fileId); //update media in save/create
    } else {
      try {
        const r = await this.productsApi.linkTestMedia(this.test.productId, this.test.id, fileId);
        this.$toasted.info(this.$t('txtSaved').toString());
        this.test.files.push(r!);
      } catch (err) {
        handleError(this.$toasted, this.$i18n, err);
      }
    }
  }

  async save(): Promise<void> {
    if (!this.test) {
      return;
    }

    this.saving = true;
    try {
      const testFinal = this.test;
      if (testFinal.id < 1) {
        const testFromServer = (await this.productsApi.createTests(testFinal.productId, [testFinal]))[0];

        //link files
        for (let i = 0; i < this.newTestFiles.length; i++) {
          const r = await this.productsApi.linkTestMedia(testFromServer.productId, testFromServer.id, this.newTestFiles[i]);
          testFinal.files.push(r!);
        }
      } else {
        await this.productsApi.updateTest(testFinal.productId, testFinal.id, testFinal);
      }

      this.$toasted.info(this.$t('txtSaved').toString());
      this.dismiss();
    } catch (err) {
      handleError(this.$toasted, this.$i18n, err);
    } finally {
      this.saving = false;
    }
  }
}
